import { IUnAssignedOrdersHook } from '@models';
import { UnassignedOrderKind, UnassignedOrderScope, UnassignedSearchField } from '@services';
import { useStore } from '@stores';
import { getDateRangeFromPreset } from '@utils';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** What the user has typed but not yet asked for. */
interface SearchDraft {
  /** Which column `text` is aimed at - the card's identity: vehicle, customer or build. */
  field: UnassignedSearchField;
  text: string;
  /** The work itself. Its own field so it narrows whatever the box above already found. */
  title: string;
  status: string[];
  /** Regular jobs, build legs, or both. */
  kind: UnassignedOrderKind;
  /**
   * The list's own date - drop-off or job date, by scope. Kept as the preset, not the bounds -
   * see IUnAssignedOrdersHook.
   */
  datePreset?: string;
  dateFrom?: string;
  dateTo?: string;
}

const EMPTY_DRAFT: SearchDraft = {
  field: 'registrationNumber',
  text: '',
  title: '',
  status: [],
  kind: 'All',
};

/**
 * Both ends become instants at apply time. A custom range arrives as two calendar days, which is
 * not a range until it is widened to the whole of them: sent as they came, the first instant of
 * the last day cut that day off, and a single day matched only a job at exactly 00:00. Local
 * midnight, not UTC - the day the user picked is the garage's day.
 */
const resolveDate = ({ datePreset, dateFrom, dateTo }: SearchDraft) => {
  if (datePreset === 'custom')
    return {
      dateFrom: dateFrom ? dayjs(dateFrom).startOf('day').toISOString() : undefined,
      dateTo: dateTo ? dayjs(dateTo).endOf('day').toISOString() : undefined,
    };
  const { startAt, endAt } = getDateRangeFromPreset(datePreset);
  return { dateFrom: startAt, dateTo: endAt };
};

const isSearching = (s: SearchDraft) =>
  !!(s.text || s.title || s.status.length || s.datePreset || s.kind !== 'All');

export const useUnAssignedServiceOrders = (
  mobile = false,
  /**
   * Whether the Unscheduled | Unassigned badges are on screen. They are the only thing the counts
   * are for, so where the switch is not offered - the phone, the week and month grids - they are
   * not asked for either.
   */
  scopeBadges = false
): IUnAssignedOrdersHook & { loadMore: () => void; hasMore: boolean } => {
  const { orderStore, authenticationStore } = useStore();
  const {
    unAssignedItems,
    unAssignedTotalCount,
    unAssignedSkipCount,
    unAssignedLoading,
    unAssignedError,
    unAssignedMaxResultCount,
    unAssignedCounts,
    setUnAssignedSkipCount,
    setUnassignedPageChange,
  } = orderStore;

  const iAmManager =
    (authenticationStore.currentUser?.role &&
      ['Manager', 'ManagerTechnician'].includes(authenticationStore.currentUser.role)) ||
    false;

  // Which list. A mode, not a search: it applies straight away, and the filters below carry over
  // (a VRM typed to find a job is as useful on the other list). The phone only has the first.
  const [scope, setScopeValue] = useState<UnassignedOrderScope>('Unscheduled');

  // Two copies on purpose. The draft moves with every keystroke and every dropdown; only what has
  // been searched for reaches the API, so picking a column - or typing half a registration - is
  // not a request.
  const [draft, setDraft] = useState<SearchDraft>(EMPTY_DRAFT);
  const [applied, setApplied] = useState<SearchDraft>(EMPTY_DRAFT);

  // Everything but the scope. The list adds it below; the badges deliberately leave it off, since
  // they are one count per scope and the API applies these same filters once for each.
  const countFilters = useMemo(
    () => ({
      kind: applied.kind,
      status: applied.status.length ? applied.status.join(',') : null,
      title: applied.title || undefined,
      ...resolveDate(applied),
      // The term goes into the one column it was aimed at, so the API filters that column and no
      // other. An empty term leaves the key undefined, which is no filter at all.
      [applied.field]: applied.text || undefined,
    }),
    [applied]
  );

  const filters = useMemo(() => ({ scope, ...countFilters }), [scope, countFilters]);

  // The badges answer the same question as the list, so they are re-asked whenever the filter
  // changes: a tab reading 9 over a list of 4 was taken as the list having lost cards.
  useEffect(() => {
    if (!iAmManager || !scopeBadges) return;
    orderStore.getUnAssignedCount(countFilters);
  }, [orderStore, iAmManager, scopeBadges, countFilters]);

  const mobilePageSize = 3;
  const mobileSkip = useRef(0);
  const [mobileLoading, setMobileLoading] = useState(false);

  useEffect(() => {
    if (mobile) {
      mobileSkip.current = 0;
      orderStore.clearUnAssignedItems();
      iAmManager &&
        orderStore.getUnAssignedItems({ ...filters, maxResultCount: mobilePageSize, skipCount: 0 });
    } else {
      iAmManager &&
        orderStore.getUnAssignedItems({
          ...filters,
          maxResultCount: unAssignedMaxResultCount,
          skipCount: unAssignedSkipCount,
        });
    }

    // Results accumulate in a Map, so the previous set has to go before the next one lands -
    // otherwise a narrowed filter would still show everything it just excluded.
    return () => orderStore.clearUnAssignedItems();
  }, [
    orderStore,
    mobile ? null : unAssignedSkipCount,
    mobile ? null : unAssignedMaxResultCount,
    iAmManager,
    filters,
  ]);

  const items = Array.from(unAssignedItems.values());
  const hasMore = items.length < unAssignedTotalCount;

  const loadMore = useCallback(() => {
    if (!mobile || mobileLoading || !hasMore) return;
    const nextSkip = mobileSkip.current + mobilePageSize;
    mobileSkip.current = nextSkip;
    setMobileLoading(true);
    orderStore
      .getUnAssignedItems({ ...filters, maxResultCount: mobilePageSize, skipCount: nextSkip })
      .then(() => setMobileLoading(false));
  }, [mobile, mobileLoading, hasMore, orderStore, filters]);

  /**
   * Every search goes back to the first page, or a narrowed list lands on a page that is now
   * empty. Through setUnassignedPageChange rather than setUnAssignedSkipCount, because the
   * store keeps a page number alongside the skip count and the two must not disagree.
   */
  const resetPaging = useCallback(() => {
    mobileSkip.current = 0;
    setUnassignedPageChange(1);
  }, [setUnassignedPageChange]);

  const setScope = useCallback(
    (value: UnassignedOrderScope) => {
      setScopeValue(value);
      resetPaging();
    },
    [resetPaging]
  );

  /** Switching column drops the term with it: the box holds one filter, not one per field. */
  const setSearchField = useCallback(
    (field: UnassignedSearchField) => setDraft((d) => ({ ...d, field, text: '' })),
    []
  );

  const setSearchText = useCallback((text: string) => setDraft((d) => ({ ...d, text })), []);
  const setTitle = useCallback((title: string) => setDraft((d) => ({ ...d, title })), []);
  const setStatus = useCallback((status: string[]) => setDraft((d) => ({ ...d, status })), []);

  // Nothing to search a build by once builds are excluded, so the identity box falls back to the
  // plate rather than keep a build term that can no longer match anything.
  const setKind = useCallback(
    (kind: UnassignedOrderKind) =>
      setDraft((d) =>
        kind === 'Regular' && d.field === 'build'
          ? { ...d, kind, field: 'registrationNumber', text: '' }
          : { ...d, kind }
      ),
    []
  );

  // Leaving 'custom' drops its bounds with it, the way switching column drops the term: a stale
  // custom range must not come back the next time 'custom' is picked.
  const setDatePreset = useCallback(
    (datePreset?: string) =>
      setDraft((d) =>
        datePreset === 'custom'
          ? { ...d, datePreset }
          : { ...d, datePreset, dateFrom: undefined, dateTo: undefined }
      ),
    []
  );
  const setDateRange = useCallback(
    (dateFrom?: string, dateTo?: string) => setDraft((d) => ({ ...d, dateFrom, dateTo })),
    []
  );

  const applySearch = useCallback(() => {
    setApplied({ ...draft, text: draft.text.trim(), title: draft.title.trim() });
    resetPaging();
  }, [draft, resetPaging]);
  /**
   * Pins the list to one day - the one the calendar is showing - or lets it go again. Sets the
   * draft and the applied search together: unlike the filters below it, this is not something the
   * user is still typing, so waiting for Search would leave the panel showing another day.
   */
  const syncToDay = useCallback(
    (day?: string) => {
      const dates = day
        ? { datePreset: 'custom', dateFrom: day, dateTo: day }
        : { datePreset: undefined, dateFrom: undefined, dateTo: undefined };
      setDraft((d) => ({ ...d, ...dates }));
      setApplied((a) => ({ ...a, ...dates }));
      resetPaging();
    },
    [resetPaging]
  );

  const clearSearch = useCallback(() => {
    setDraft((d) => ({ ...EMPTY_DRAFT, field: d.field }));
    setApplied((a) => ({ ...EMPTY_DRAFT, field: a.field }));
    resetPaging();
  }, [resetPaging]);

  const hasSearch = isSearching(applied) || isSearching(draft);

  return {
    unAssignedItems: items,
    unAssignedMaxResultCount: mobile ? mobilePageSize : unAssignedMaxResultCount,
    unAssignedSkipCount: mobile ? mobileSkip.current : unAssignedSkipCount,
    unAssignedLoading: mobile ? mobileLoading : unAssignedLoading,
    unAssignedError,
    unAssignedTotalCount,
    setUnAssignedSkipCount,
    setUnassignedPageChange,
    scope,
    setScope,
    counts: unAssignedCounts,
    kind: draft.kind,
    setKind,
    searchField: draft.field,
    setSearchField,
    searchText: draft.text,
    setSearchText,
    title: draft.title,
    setTitle,
    status: draft.status,
    setStatus,
    datePreset: draft.datePreset,
    setDatePreset,
    dateFrom: draft.dateFrom,
    dateTo: draft.dateTo,
    setDateRange,
    syncToDay,
    applySearch,
    clearSearch,
    hasSearch,
    loadMore,
    hasMore,
  };
};
